import { useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type Unavailability,
  type UnavailabilityStatus,
  approveUnavailability,
  listAllUnavailabilities,
  rejectUnavailability,
} from "@/api/unavailabilities";
import { PageHeader } from "@/components/PageHeader";

const STATUS_FILTERS: (UnavailabilityStatus | "ALL")[] = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "CANCELLED",
  "ALL",
];

function statusColor(s: UnavailabilityStatus): "default" | "success" | "warning" | "error" {
  if (s === "APPROVED") return "success";
  if (s === "REJECTED") return "error";
  if (s === "PENDING") return "warning";
  return "default";
}

export function UnavailabilitiesAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<UnavailabilityStatus | "ALL">("PENDING");
  const [decisionTarget, setDecisionTarget] = useState<{
    row: Unavailability;
    kind: "approve" | "reject";
  } | null>(null);
  const [decisionReason, setDecisionReason] = useState("");

  const list = useQuery({
    queryKey: ["unavailabilities", filter],
    queryFn: () =>
      listAllUnavailabilities(filter === "ALL" ? {} : { status: filter }),
  });

  const decideMutation = useMutation({
    mutationFn: () => {
      if (!decisionTarget) throw new Error("no_target");
      const fn =
        decisionTarget.kind === "approve" ? approveUnavailability : rejectUnavailability;
      return fn(decisionTarget.row.id, decisionReason || undefined);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["unavailabilities"] });
      setDecisionTarget(null);
      setDecisionReason("");
    },
  });

  return (
    <Box>
      <PageHeader
        helpTerm="indisponibilite"
        title={t("unavailabilities.adminTitle")}
        subtitle={t("unavailabilities.adminSubtitle")}
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t("unavailabilities.statusFilter")}</InputLabel>
          <Select
            label={t("unavailabilities.statusFilter")}
            value={filter}
            onChange={(e) => setFilter(e.target.value as UnavailabilityStatus | "ALL")}
          >
            {STATUS_FILTERS.map((s) => (
              <MenuItem key={s} value={s}>
                {s === "ALL" ? t("unavailabilities.statusAll") : t(`unavailabilities.status.${s}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {list.isLoading && <CircularProgress />}
      {list.isError && (
        <Alert severity="error">{(list.error as Error).message}</Alert>
      )}
      {list.data && list.data.length === 0 && (
        <Alert severity="info">{t("unavailabilities.empty")}</Alert>
      )}

      {list.data && list.data.length > 0 && (
        <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("unavailabilities.practitioner")}</TableCell>
              <TableCell>{t("unavailabilities.kind")}</TableCell>
              <TableCell>{t("unavailabilities.from")}</TableCell>
              <TableCell>{t("unavailabilities.to")}</TableCell>
              <TableCell>{t("unavailabilities.reason")}</TableCell>
              <TableCell>{t("unavailabilities.status_col")}</TableCell>
              <TableCell align="right">{t("orgs.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.data.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.practitioner_display_name ?? r.practitioner_id}</TableCell>
                <TableCell>
                  <Chip size="small" label={t(`unavailabilities.kindLabel.${r.kind}`)} />
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{r.date_from}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{r.date_to}</TableCell>
                <TableCell sx={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.reason ?? "—"}
                </TableCell>
                <TableCell>
                  <Chip size="small" color={statusColor(r.status)} label={t(`unavailabilities.status.${r.status}`)} />
                </TableCell>
                <TableCell align="right">
                  {r.status === "PENDING" ? (
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Button
                        size="small"
                        color="success"
                        startIcon={<CheckIcon />}
                        onClick={() => setDecisionTarget({ row: r, kind: "approve" })}
                      >
                        {t("unavailabilities.approve")}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<CloseIcon />}
                        onClick={() => setDecisionTarget({ row: r, kind: "reject" })}
                      >
                        {t("unavailabilities.reject")}
                      </Button>
                    </Stack>
                  ) : (
                    r.decision_reason ?? "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      )}

      <Dialog
        open={Boolean(decisionTarget)}
        onClose={() => setDecisionTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {decisionTarget?.kind === "approve"
            ? t("unavailabilities.approveTitle")
            : t("unavailabilities.rejectTitle")}
        </DialogTitle>
        <DialogContent>
          <TextField
            label={t("unavailabilities.decisionReason")}
            value={decisionReason}
            onChange={(e) => setDecisionReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecisionTarget(null)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            color={decisionTarget?.kind === "approve" ? "success" : "error"}
            onClick={() => decideMutation.mutate()}
            disabled={decideMutation.isPending}
          >
            {decisionTarget?.kind === "approve"
              ? t("unavailabilities.approve")
              : t("unavailabilities.reject")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
